import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Constants from "../Constants.json";
import { navigateToStockDashboard } from "../utils/stockDashboardNav";
import MarketPageSubNav from "./MarketPageSubNav";

export const INV_NAV_ROW_H = 52;
export const INV_MARKET_SUBNAV_H = 40;
export const invChromeHeight = (withSubNav) =>
  withSubNav ? INV_NAV_ROW_H + INV_MARKET_SUBNAV_H : INV_NAV_ROW_H;

const C = { gold: "#b59a5b" };
const NAV_TXT = "rgba(255,255,255,.62)";
const NAV0 = "#2e574f";
const NAV1 = "#3d7068";

const NAV = [
  { id: "home", label: "Accueil", href: "/capital" },
  { id: "market", label: "Marché", children: [
      { id: "synth", label: "Synthèse", href: "/capital" },
      { id: "flux", label: "Flux", href: "/marketflow" },
      { id: "leaders", label: "Leaders", href: "/" },
    ]},
  { id: "strategies", label: "Stratégies", href: "/capital/strategies" },
  { id: "screener", label: "Screener", href: "/capital/screener" },
];

function pathActive(pathname, href) {
  if (href === "/") return pathname === "/";
  if (href === "/capital") return pathname === "/capital" || pathname === "/capital/";
  if (href === "/marketflow") return pathname === "/marketflow";
  return pathname === href || pathname.startsWith(href + "/");
}

function groupActive(pathname, item) {
  if (item.href && pathActive(pathname, item.href)) return true;
  if (item.children) return item.children.some((c) => pathActive(pathname, c.href));
  return false;
}

function NavSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  useEffect(() => {
    const query = String(q || "").trim();
    if (query.length < 2) { setHits([]); setLoading(false); return; }
    setLoading(true);
    const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    const base = isLocal ? "http://127.0.0.1:8000/api" : (Constants.BACKEND_SERVER_BASE_URL || "").replace(/\/$/, "");
    if (!base) { setHits([]); setLoading(false); return; }
    const id = window.setTimeout(() => {
      fetch(base + "/capitalflow/search?q=" + encodeURIComponent(query))
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((j) => setHits(Array.isArray(j?.results) ? j.results.slice(0, 6) : []))
        .catch(() => setHits([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => window.clearTimeout(id);
  }, [q]);
  const go = (sym) => { if (!sym) return; onPick(sym); setQ(""); setOpen(false); };
  return (
    <div ref={ref} style={{ position: "relative", flex: 1, maxWidth: 240, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, height: 36, background: "rgba(255,255,255,.13)", border: "1px solid rgba(255,255,255,.18)", borderRadius: 10, padding: "0 12px" }}>
        <span style={{ color: "rgba(255,255,255,.45)", fontSize: 14 }}>⌕</span>
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) go(hits[0]?.symbol || q.trim().toUpperCase()); if (e.key === "Escape") { setOpen(false); setQ(""); } }}
          placeholder="Symbole..." style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#fff", fontSize: 12, fontWeight: 600, minWidth: 0 }} />
      </div>
      {open && (q.trim() || hits.length) ? (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#fff", borderRadius: 10, border: "1px solid rgba(30,40,34,.1)", boxShadow: "0 8px 24px rgba(0,0,0,.18)", overflow: "hidden", zIndex: 300 }}>
          {loading ? <div style={{ padding: "10px 12px", fontSize: 11, color: "#6f7b74" }}>Recherche...</div> : null}
          {!loading && hits.length === 0 && q.trim().length >= 2 ? <div style={{ padding: "10px 12px", fontSize: 11, color: "#6f7b74" }}>Aucun résultat</div> : null}
          {hits.map((h, i) => (
            <button key={h.symbol || i} type="button" onClick={() => go(h.symbol)}
              style={{ display: "flex", width: "100%", padding: "9px 12px", background: "none", border: "none", borderBottom: i < hits.length - 1 ? "1px solid rgba(30,40,34,.08)" : "none", textAlign: "left", cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(181,154,91,.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
              <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11, fontWeight: 800, color: "#1f2a24" }}>{h.symbol}</div>
              {h.name ? <div style={{ fontSize: 10, color: "#6f7b74", marginLeft: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</div> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function useNavStyles() {
  useEffect(() => {
    if (document.getElementById("inv-global-nav-css")) return;
    const s = document.createElement("style");
    s.id = "inv-global-nav-css";
    s.textContent = `.inv-global-nav-desktop{display:none;}@media(min-width:768px){.inv-global-nav-desktop{display:flex;align-items:stretch;}}.inv-global-nav-mobile-btn{display:flex;}@media(min-width:768px){.inv-global-nav-mobile-btn{display:none!important;}}`;
    document.head.appendChild(s);
  }, []);
}

export default function InvelpsGlobalNav({ darkMode, onToggleDark, showMarketSubNav = false, language = "fr" }) {
  useNavStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname || "/";
  const [menu, setMenu] = useState(false);
  const [mktOpen, setMktOpen] = useState(false);
  const mktRef = useRef(null);
  const openTicker = useCallback((sym) => navigateToStockDashboard(navigate, sym, "nav"), [navigate]);
  const go = (href) => { navigate(href); setMenu(false); setMktOpen(false); };
  useEffect(() => {
    const fn = (e) => { if (mktRef.current && !mktRef.current.contains(e.target)) setMktOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  const btnStyle = (active) => ({
    background: "none", border: "none", padding: "0 14px", height: 52, fontSize: 13, fontWeight: 800,
    color: active ? "#fff" : NAV_TXT,
    borderBottom: active ? "2.5px solid " + C.gold : "2.5px solid transparent", transition: "color .15s",
    cursor: "pointer",
  });
  return (
    <nav style={{ position: "sticky", top: 0, zIndex: 200, background: "linear-gradient(150deg," + NAV0 + "," + NAV1 + ")", boxShadow: "0 2px 20px rgba(16,40,28,.24)" }}>
      <div style={{ maxWidth: 1600, margin: "0 auto", padding: "0 14px", display: "flex", alignItems: "center", height: INV_NAV_ROW_H, gap: 10 }}>
        <button type="button" onClick={() => go("/capital")} style={{ background: "none", border: "none", display: "flex", alignItems: "center", gap: 7, flexShrink: 0, padding: 0, cursor: "pointer" }}>
          <div style={{ width: 2.5, height: 24, background: C.gold, borderRadius: 2 }} />
          <span style={{ fontFamily: "Cormorant Garamond, Georgia, serif", fontSize: 18, fontWeight: 700, color: C.gold, letterSpacing: "0.12em", lineHeight: 1 }}>INVELPS</span>
        </button>
        <div className="inv-global-nav-desktop" style={{ flex: 1, height: 52 }}>
          {NAV.map((item) => item.children ? (
            <div key={item.id} ref={mktRef} style={{ position: "relative", display: "inline-flex", height: 52, alignItems: "center" }}>
              <button type="button" onClick={() => setMktOpen((o) => !o)} style={{ ...btnStyle(groupActive(pathname, item)), display: "flex", alignItems: "center", gap: 5 }}>
                {item.label} <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
              </button>
              {mktOpen ? (
                <div style={{ position: "absolute", top: 52, left: 0, background: "#fff", borderRadius: 10, border: "1px solid rgba(30,40,34,.1)", boxShadow: "0 8px 24px rgba(0,0,0,.18)", overflow: "hidden", minWidth: 140, zIndex: 300 }}>
                  {item.children.map((s) => (
                    <button key={s.id} type="button" onClick={() => go(s.href)}
                      style={{ display: "flex", width: "100%", padding: "11px 16px", background: "none", border: "none", borderBottom: "1px solid rgba(30,40,34,.08)", fontSize: 12, fontWeight: 800, color: pathActive(pathname, s.href) ? C.gold : "#1f2a24", textAlign: "left", cursor: "pointer" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(181,154,91,.08)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <button key={item.id} type="button" onClick={() => go(item.href)} style={btnStyle(groupActive(pathname, item))}>{item.label}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <NavSearch onPick={openTicker} />
        {onToggleDark ? (
          <button type="button" onClick={onToggleDark} style={{ width: 34, height: 34, borderRadius: 9, border: "1px solid rgba(255,255,255,.16)", background: "rgba(255,255,255,.10)", color: "#fff", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer" }}>
            {darkMode ? "☀" : "☾"}
          </button>
        ) : null}
        <button type="button" className="inv-global-nav-mobile-btn" onClick={() => setMenu((m) => !m)}
          style={{ width: 34, height: 34, borderRadius: 9, border: "1px solid rgba(255,255,255,.16)", background: "rgba(255,255,255,.10)", color: "#fff", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, flexShrink: 0, cursor: "pointer" }}>
          {menu ? <span style={{ fontSize: 14, lineHeight: 1 }}>✕</span> : [0, 1, 2].map((i) => <div key={i} style={{ width: 14, height: 2, borderRadius: 1, background: "rgba(255,255,255,.9)" }} />)}
        </button>
      </div>
      {showMarketSubNav ? <MarketPageSubNav variant="bar" language={language} /> : null}
      {menu ? (
        <div style={{ background: NAV0, borderTop: "1px solid rgba(255,255,255,.10)", paddingBottom: 8 }}>
          {NAV.map((item) => item.children ? (
            <div key={item.id}>
              <div style={{ padding: "11px 14px 4px", fontSize: 11, fontWeight: 800, color: C.gold, textTransform: "uppercase", letterSpacing: ".08em" }}>{item.label}</div>
              {item.children.map((s) => (
                <button key={s.id} type="button" onClick={() => go(s.href)}
                  style={{ display: "flex", width: "100%", padding: "10px 14px 10px 24px", background: "none", border: "none", color: pathActive(pathname, s.href) ? C.gold : NAV_TXT, fontSize: 13, fontWeight: 700, borderLeft: pathActive(pathname, s.href) ? "3px solid " + C.gold : "3px solid transparent", cursor: "pointer" }}>
                  {s.label}
                </button>
              ))}
            </div>
          ) : (
            <button key={item.id} type="button" onClick={() => go(item.href)}
              style={{ display: "flex", width: "100%", padding: "13px 14px", background: "none", border: "none", color: groupActive(pathname, item) ? C.gold : NAV_TXT, fontSize: 14, fontWeight: 800, borderLeft: groupActive(pathname, item) ? "3px solid " + C.gold : "3px solid transparent", cursor: "pointer" }}>
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </nav>
  );
}
